import React from "react";
import { SkRect, SkEllipse, SkCircle, SkLine, SkPolygon, SkText } from "../prim";

/**
 * Anivaze v3 — skin-aware OBJECT / PROP actors.
 *
 * Each actor is `skName(x, y, t, scale?, …opts) → JSX`, composed ONLY from the
 * skinnable primitives in `../prim`. Animate via `<g transform>` so hand-drawn
 * seeds stay stable. Seed range: 1400–1599. See design-plan/v3/PLAN.md §4–5.
 */

// ─── skBook ──────────────────────────────────────────────────────────────────
/** An open book with pages; gentle up/down hover from t. */
export function skBook(x: number, y: number, t: number, scale = 1): React.ReactNode {
  const hover = Math.sin(t * 1.8) * 3;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <g transform={`translate(0 ${hover})`}>
        {/* left page */}
        <SkRect x={-50} y={-34} w={48} h={54} rx={2} fill="#fef9c3" seed={1400} />
        {/* right page */}
        <SkRect x={2} y={-34} w={48} h={54} rx={2} fill="#fff" seed={1401} />
        {/* spine */}
        <SkRect x={-2} y={-34} w={4} h={54} fill="#a16207" seed={1402} />
        {/* left page lines */}
        <SkLine x1={-42} y1={-20} x2={-10} y2={-20} stroke="#d1d5db" strokeWidth={1.5} seed={1403} />
        <SkLine x1={-42} y1={-10} x2={-10} y2={-10} stroke="#d1d5db" strokeWidth={1.5} seed={1404} />
        <SkLine x1={-42} y1={0}   x2={-10} y2={0}   stroke="#d1d5db" strokeWidth={1.5} seed={1405} />
        <SkLine x1={-42} y1={10}  x2={-10} y2={10}  stroke="#d1d5db" strokeWidth={1.5} seed={1406} />
        {/* right page lines */}
        <SkLine x1={10}  y1={-20} x2={42} y2={-20}  stroke="#d1d5db" strokeWidth={1.5} seed={1407} />
        <SkLine x1={10}  y1={-10} x2={42} y2={-10}  stroke="#d1d5db" strokeWidth={1.5} seed={1408} />
        <SkLine x1={10}  y1={0}   x2={42} y2={0}    stroke="#d1d5db" strokeWidth={1.5} seed={1409} />
        <SkLine x1={10}  y1={10}  x2={42} y2={10}   stroke="#d1d5db" strokeWidth={1.5} seed={1410} />
        {/* cover bottom strip */}
        <SkRect x={-50} y={20} w={100} h={10} rx={2} fill="#a16207" seed={1411} />
      </g>
    </g>
  );
}

// ─── skLightbulb ─────────────────────────────────────────────────────────────
/** A bulb that flickers on — glow rays pulse from t. */
export function skLightbulb(x: number, y: number, t: number, scale = 1): React.ReactNode {
  const glow = 0.6 + Math.abs(Math.sin(t * 3.2)) * 0.4;
  const rayLen = 14 + Math.abs(Math.sin(t * 3.2)) * 8;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* glow rays */}
      {Array.from({ length: 8 }, (_, i) => {
        const a = (i / 8) * Math.PI * 2;
        const r1 = 28;
        const r2 = r1 + rayLen;
        return (
          <SkLine
            key={i}
            x1={Math.cos(a) * r1} y1={Math.sin(a) * r1 - 6}
            x2={Math.cos(a) * r2} y2={Math.sin(a) * r2 - 6}
            stroke="#fde047"
            strokeWidth={2}
            opacity={glow}
            seed={1420 + i}
          />
        );
      })}
      {/* bulb body */}
      <SkCircle cx={0} cy={-14} r={24} fill="#fde047" opacity={glow} seed={1430} />
      {/* neck / base */}
      <SkRect x={-10} y={8} w={20} h={8} rx={2} fill="#d1d5db" seed={1431} />
      <SkRect x={-8}  y={16} w={16} h={6} rx={1} fill="#9ca3af" seed={1432} />
      {/* filament */}
      <SkLine x1={0} y1={-4} x2={-5} y2={-16} stroke="#f59e0b" strokeWidth={1.5} seed={1433} />
      <SkLine x1={-5} y1={-16} x2={5} y2={-22} stroke="#f59e0b" strokeWidth={1.5} seed={1434} />
      <SkLine x1={5} y1={-22} x2={0} y2={-30}  stroke="#f59e0b" strokeWidth={1.5} seed={1435} />
    </g>
  );
}

// ─── skBox ───────────────────────────────────────────────────────────────────
/** A labelled crate/box with lid seam and corner braces. */
export function skBox(
  x: number,
  y: number,
  _t: number,
  scale = 1,
  label = "BOX",
): React.ReactNode {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* body */}
      <SkRect x={-40} y={-36} w={80} h={60} rx={3} fill="#fbbf24" seed={1440} />
      {/* lid top strip */}
      <SkRect x={-40} y={-36} w={80} h={16} rx={3} fill="#f59e0b" seed={1441} />
      {/* horizontal lid seam */}
      <SkLine x1={-40} y1={-20} x2={40} y2={-20} stroke="#92400e" strokeWidth={2} seed={1442} />
      {/* vertical center seam */}
      <SkLine x1={0} y1={-36} x2={0} y2={24} stroke="#92400e" strokeWidth={2} seed={1443} />
      {/* corner braces (top-left, top-right) */}
      <SkLine x1={-40} y1={-36} x2={-28} y2={-24} stroke="#92400e" strokeWidth={1.5} seed={1444} />
      <SkLine x1={40}  y1={-36} x2={28}  y2={-24} stroke="#92400e" strokeWidth={1.5} seed={1445} />
      {/* label */}
      <SkText x={0} y={6} size={14} weight={700} fill="#7c2d12">{label}</SkText>
    </g>
  );
}

// ─── skArrow ─────────────────────────────────────────────────────────────────
/** A bold directional arrow; direction "right" | "left" | "up" | "down". Subtle scale pulse. */
export function skArrow(
  x: number,
  y: number,
  t: number,
  scale = 1,
  direction: "right" | "left" | "up" | "down" = "right",
): React.ReactNode {
  const pulse = 1 + Math.sin(t * 2.5) * 0.06;
  const rotMap = { right: 0, down: 90, left: 180, up: 270 };
  const rot = rotMap[direction];
  return (
    <g transform={`translate(${x} ${y}) scale(${scale}) rotate(${rot})`}>
      <g transform={`scale(${pulse})`}>
        {/* shaft */}
        <SkRect x={-36} y={-8} w={52} h={16} rx={4} fill="#3b82f6" seed={1450} />
        {/* arrowhead */}
        <SkPolygon
          points={[[16, -24], [44, 0], [16, 24]]}
          fill="#2563eb"
          seed={1451}
        />
      </g>
    </g>
  );
}

// ─── skFlag ──────────────────────────────────────────────────────────────────
/** A flag on a pole; cloth waves via skew transform driven by t. */
export function skFlag(
  x: number,
  y: number,
  t: number,
  scale = 1,
  color = "#ef4444",
): React.ReactNode {
  // wave: horizontal skew of the cloth panel
  const skewX = Math.sin(t * 2.8) * 10;
  const skewY = Math.cos(t * 2.8) * 4;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* pole */}
      <SkLine x1={-20} y1={60} x2={-20} y2={-70} stroke="#6b7280" strokeWidth={4} seed={1460} />
      {/* ball finial */}
      <SkCircle cx={-20} cy={-74} r={5} fill="#fbbf24" seed={1461} />
      {/* cloth — wave via skewX/Y on the group */}
      <g transform={`translate(-20 -60) skewX(${skewX}) skewY(${skewY})`}>
        <SkRect x={0} y={0} w={64} h={38} fill={color} seed={1462} />
        {/* simple stripe decoration */}
        <SkRect x={0} y={12} w={64} h={14} fill="#ffffff" opacity={0.35} seed={1463} />
      </g>
    </g>
  );
}

// ─── skClock ─────────────────────────────────────────────────────────────────
/** A clock face; minute hand completes a revolution per 2π of t, hour hand moves 12× slower. */
export function skClock(x: number, y: number, t: number, scale = 1): React.ReactNode {
  const minuteRot = (t * 60) % 360;          // one full turn per ~6.28 t-units
  const hourRot   = (t * 5) % 360;           // 12× slower
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* outer face */}
      <SkCircle cx={0} cy={0} r={44} fill="#f8fafc" seed={1470} />
      {/* inner face detail */}
      <SkCircle cx={0} cy={0} r={38} fill="#e2e8f0" seed={1471} />
      {/* hour markers */}
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
        const r1 = 30, r2 = 38;
        return (
          <SkLine
            key={i}
            x1={Math.cos(a) * r1} y1={Math.sin(a) * r1}
            x2={Math.cos(a) * r2} y2={Math.sin(a) * r2}
            stroke="#64748b"
            strokeWidth={i % 3 === 0 ? 3 : 1.5}
            seed={1480 + i}
          />
        );
      })}
      {/* hour hand */}
      <g transform={`rotate(${hourRot})`}>
        <SkLine x1={0} y1={4} x2={0} y2={-22} stroke="#1e293b" strokeWidth={4} seed={1492} />
      </g>
      {/* minute hand */}
      <g transform={`rotate(${minuteRot})`}>
        <SkLine x1={0} y1={5} x2={0} y2={-32} stroke="#334155" strokeWidth={2.5} seed={1493} />
      </g>
      {/* center pivot */}
      <SkCircle cx={0} cy={0} r={4} fill="#ef4444" seed={1494} />
    </g>
  );
}

// ─── skBall ──────────────────────────────────────────────────────────────────
/** A ball that bounces; y-offset derived from |sin(t)|, squash on landing. */
export function skBall(
  x: number,
  y: number,
  t: number,
  scale = 1,
  color = "#f87171",
): React.ReactNode {
  const bounce = Math.abs(Math.sin(t * 2.6));      // 0 = ground, 1 = peak
  const offY   = -(bounce * 60);
  // squash when near ground, stretch near peak
  const scaleY = 0.75 + bounce * 0.5;
  const scaleX = 1 + (1 - bounce) * 0.3;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* shadow */}
      <SkEllipse
        cx={0} cy={4}
        rx={18 + (1 - bounce) * 10}
        ry={4  - bounce * 2}
        fill="#00000033"
        opacity={0.5 - bounce * 0.3}
        seed={1500}
      />
      {/* ball */}
      <g transform={`translate(0 ${offY}) scale(${scaleX} ${scaleY})`}>
        <SkCircle cx={0} cy={0} r={22} fill={color} seed={1501} />
        {/* specular highlight */}
        <SkEllipse cx={-7} cy={-8} rx={6} ry={4} fill="#ffffff" opacity={0.5} seed={1502} />
      </g>
    </g>
  );
}

// ─── skCup ───────────────────────────────────────────────────────────────────
/** A mug/cup with a handle and three rising steam wisps. */
export function skCup(
  x: number,
  y: number,
  t: number,
  scale = 1,
  color = "#7c3aed",
): React.ReactNode {
  // Each wisp rises and fades; staggered by index offset
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* steam wisps — three columns, staggered phase */}
      {([[-14, 0], [0, 1.1], [14, 2.2]] as [number, number][]).map(([wx, phase], i) => {
        const rise   = ((t * 0.9 + phase) % (Math.PI * 2));
        const wispY  = -Math.sin(rise) * 24;
        const wispX  = Math.sin(rise * 2) * 5;
        const fade   = Math.max(0, Math.sin(rise * 0.5));
        return (
          <g key={i} transform={`translate(${wx + wispX} ${-46 + wispY})`} opacity={fade * 0.7}>
            <SkEllipse cx={0} cy={0} rx={4} ry={9} fill="#cbd5e1" seed={1510 + i} />
          </g>
        );
      })}
      {/* cup body (trapezoid approximated with polygon) */}
      <SkPolygon
        points={[[-28, 0], [-22, -48], [22, -48], [28, 0]]}
        fill={color}
        seed={1520}
      />
      {/* rim top */}
      <SkEllipse cx={0} cy={-48} rx={22} ry={6} fill="#a78bfa" seed={1521} />
      {/* liquid surface */}
      <SkEllipse cx={0} cy={-44} rx={18} ry={4} fill="#c4b5fd" seed={1522} />
      {/* base */}
      <SkEllipse cx={0} cy={0} rx={28} ry={6} fill="#6d28d9" seed={1523} />
      {/* handle */}
      <SkEllipse cx={34} cy={-24} rx={10} ry={14} fill="none" stroke={color} strokeWidth={4} seed={1524} />
      {/* handle inner cutout — white ellipse to fake a hole */}
      <SkEllipse cx={34} cy={-24} rx={5} ry={8} fill="#ffffff" seed={1525} />
    </g>
  );
}
